import { memo, useRef } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

import Link from 'next/link';

import { FiDollarSign } from '@react-icons/all-files/fi/FiDollarSign';
import { BiBriefcase } from '@react-icons/all-files/bi/BiBriefcase';
import { BiWallet } from '@react-icons/all-files/bi/BiWallet';

import { formatNumber } from '@mais-retorno-front/helpers/util-helpers';
import { SponsoredHoc } from '../sponsored-hoc';
import { ManagerDataDialog } from '@mais-retorno-front/modules/sheet-fund/manager-data-alert';
import Button from '@core/design-system/button';
import { Card } from '@core/design-system/card';
import { ISheetFundsManagerDetails } from '../retorno-ads/sponsored-sheets';
import { SponsoredAssetDescription } from '../retorno-ads/sponsored-asset-description';
import { SponsoredInvestmentModal } from '../retorno-ads/sponsored-investment-modal';

interface ISheetFundsPolices {
  fundManager: ISheetFundsManagerDetails;
  fundPolices: {
    fund: {
      nicename: string;
      cnpj: number;
    };
    target_group: string;
    objective: string;
    investment_policy: string;
    administration_fee: {
      min: number;
      max: number;
      isMoney: boolean;
    };
    minimum_application_value: number;
    fund_perfomance_fee: {
      fee: string;
      exists: boolean;
      index_reference: string;
      desc_performance_fee: string;
    };
    days_for_quote_redemption: string;
    days_for_money_redemption: string;
    permanency_minimum_amount: number;
    bought_quote_conversion_period: number;
    bought_quote_conversion_type: string;
    cancelation_quotation_type: string;
  };
  investmentList: { label: string; link: string }[] | null;
  isSponsored: boolean;
  monthlyReport: string | null;
  sponsoredVideo: string | null;
  sponsoredYoutubeVideo: string | null;
  managerSlug: string;
  fundDescriptionDocument: string;
  fundDescription: string;
  hasComplementaryData?: boolean;
}

export const SheetFundsPolices = memo(function SheetFundsPolices({
  fundManager,
  investmentList,
  fundPolices,
  isSponsored,
  sponsoredVideo,
  monthlyReport,
  sponsoredYoutubeVideo,
  managerSlug,
  fundDescriptionDocument,
  fundDescription = '<p>-<p>',
  hasComplementaryData,
}: ISheetFundsPolices) {
  const theme = useTheme();
  const dialogRef = useRef(null);

  const handleOpenDialog = () => {
    dialogRef.current.handleOpenDialog();
  };

  return (
    <>
      <Grid container my={3} spacing={1}>
        <Grid item xs={12} md={12} lg={6}>
          <Card cardProps={{ sx: { height: '100%' } }}>
            <Typography component="h3" variant="h5" fontWeight={700} mb={1}>
              {fundManager?.name || '-'}
            </Typography>

            {hasComplementaryData && (
              <Stack direction="row" flexWrap="wrap">
                <Typography component="p" variant="body2">
                  As informações desta página contém dados complementares e/ou
                  editados enviados pelo gestor responsável pelo fundo
                </Typography>
                <Button
                  onClick={handleOpenDialog}
                  sx={{
                    p: 0,
                    textTransform: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Saiba mais.
                </Button>
              </Stack>
            )}

            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <Typography component="p" variant="body2" fontWeight={700}>
                  Diretor responsável
                </Typography>
                <Typography component="p" variant="body2">
                  {fundManager?.director || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="p" variant="body2" fontWeight={700}>
                  E-mail
                </Typography>
                <Typography component="p" variant="body2">
                  {fundManager?.email || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="p" variant="body2" fontWeight={700}>
                  Site
                </Typography>
                <Typography component="p" variant="body2">
                  {fundManager?.site || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="p" variant="body2" fontWeight={700}>
                  Telefone
                </Typography>
                <Typography component="p" variant="body2">
                  {fundManager?.phone || '-'}
                </Typography>
              </Grid>
            </Grid>

            <SponsoredHoc isSponsored={isSponsored}>
              <Grid
                container
                spacing={1}
                alignItems="stretch"
                width="100%"
                mt={3}
                sx={{ '@media print': { display: 'none' } }}
              >
                {investmentList?.length > 0 && (
                  <Grid item xs={12} xl={4}>
                    <SponsoredInvestmentModal investmentList={investmentList} />
                  </Grid>
                )}

                {Boolean(fundDescriptionDocument) && (
                  <Grid item xs={12} xl={4}>
                    <Button
                      variant="outlined"
                      color="primary"
                      target="_blank"
                      component="a"
                      href={fundDescriptionDocument}
                      LinkComponent={Link}
                      sx={{
                        textTransform: 'none',
                        width: '100%',
                      }}
                    >
                      Baixar Lâmina do Fundo
                    </Button>
                  </Grid>
                )}

                <Grid item xs={12} xl={4}>
                  <Button
                    target="_blank"
                    component="a"
                    href={monthlyReport ? monthlyReport : '#'}
                    variant="outlined"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      width: '100%',
                    }}
                  >
                    Baixar Relatório Mensal
                  </Button>
                </Grid>
              </Grid>
            </SponsoredHoc>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} lg={2}>
          <Card cardProps={{ sx: { height: '100%' } }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: theme.palette.mediumNeutral.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '4px',
                  padding: '3px',
                }}
              >
                <FiDollarSign size={18} />
              </Box>
              <Typography
                component="h4"
                variant="body1"
                fontWeight={700}
                sx={{ textTransform: 'uppercase' }}
              >
                Movimentação
              </Typography>

              <Box>
                <Typography component="p" variant="body2">
                  Aporte mínimo:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.minimum_application_value
                    ? formatNumber(fundPolices?.minimum_application_value, {
                        type: 'money',
                      })
                    : '-'}
                </Typography>
              </Box>

              <Box>
                <Typography component="p" variant="body2">
                  Valor mínimo para permanência:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.permanency_minimum_amount
                    ? formatNumber(fundPolices?.permanency_minimum_amount, {
                        type: 'money',
                      })
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} lg={2}>
          <Card cardProps={{ sx: { height: '100%' } }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: theme.palette.mediumNeutral.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '4px',
                  padding: '3px',
                }}
              >
                <BiBriefcase size={18} />
              </Box>
              <Typography
                component="h4"
                variant="body1"
                fontWeight={700}
                sx={{ textTransform: 'uppercase' }}
              >
                Taxas
              </Typography>

              <Box>
                <Typography component="p" variant="body2">
                  Taxa de Adm:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.administration_fee?.min
                    ? formatNumber(fundPolices?.administration_fee?.min, {
                        type: 'percent',
                      })
                    : '-'}
                </Typography>
              </Box>

              <Box>
                <Typography component="p" variant="body2">
                  Taxa de Adm máx:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.administration_fee?.max
                    ? formatNumber(fundPolices?.administration_fee?.max, {
                        type: 'percent',
                      })
                    : '-'}
                </Typography>
              </Box>

              <Box>
                <Typography component="p" variant="body2">
                  Taxa de Performance:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.fund_perfomance_fee?.desc_performance_fee
                    ? fundPolices?.fund_perfomance_fee?.desc_performance_fee
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4} lg={2}>
          <Card cardProps={{ sx: { height: '100%' } }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: theme.palette.mediumNeutral.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '4px',
                  padding: '3px',
                }}
              >
                <BiWallet size={18} />
              </Box>
              <Typography
                component="h4"
                variant="body1"
                fontWeight={700}
                sx={{ textTransform: 'uppercase' }}
              >
                Resgate
              </Typography>

              <Box>
                <Typography component="p" variant="body2">
                  Prazo de cotização:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.days_for_quote_redemption}
                </Typography>
              </Box>

              <Box>
                <Typography component="p" variant="body2">
                  Liquidação financeira:
                </Typography>
                <Typography component="p" variant="body2" fontWeight={700}>
                  {fundPolices?.days_for_money_redemption}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <SponsoredAssetDescription
          description={fundDescription}
          sponsoredVideo={sponsoredVideo}
          sponsoredYoutubeVideo={sponsoredYoutubeVideo}
          isSponsored={isSponsored}
        />
      </Grid>

      <ManagerDataDialog ref={dialogRef} />
    </>
  );
});
